/**
 * This react helmt code is adapted from
 * https://themeteorchef.com/tutorials/reusable-seo-with-react-helmet.
 *
 * A great tutorial explaining how to setup a robust version of an
 * SEO friendly react-helmet instance.
 *
 *
 * Use the Helmt on pages to generate SEO and meta content!
 *
 * Usage:
 * <SEO
 *   title={title}
 *   description={description}
 *   image={image}
 * />
 *
 */

import React from "react";
import { Location } from "@reach/router";
import { Helmet } from "react-helmet";
import { graphql, useStaticQuery } from "gatsby";

interface HelmetProps {
  articlepathName?: string;
  authorName?: string;
  authorsBio?: string;
  authorsSlug?: string;
  canonicalUrl?: string;
  dateforSEO?: string;
  description?: string;
  image?: string;
  isBlogPost: false;
  pathname: string;
  published?: string;
  timeToRead?: string;
  title: string;
  isSecret: false;
  location: Location;
}

const seoQuery = graphql`
  {
    allSite {
      edges {
        node {
          siteMetadata {
            description
            social {
              url
              name
            }
            siteUrl
            title
            name
          }
        }
      }
    }
  }
`;

const themeUIDarkModeWorkaroundScript = [
  {
    type: "text/javascript",
    innerHTML: `
    (function() {
      try {
        var mode = localStorage.getItem('theme-ui-color-mode');
        if (!mode) {
          localStorage.setItem('theme-ui-color-mode', 'light');
        }
      } catch (e) {}
    })();
  `,
  },
];

const SEO: React.FC<HelmetProps> = ({
  articlepathName,
  authorName,
  authorsBio,
  authorsSlug,
  canonicalUrl,
  children,
  dateforSEO,
  description,
  image,
  isBlogPost,
  pathname,
  published,
  timeToRead,
  title,
  isSecret,
  location,
}) => {
  const results = useStaticQuery(seoQuery);
  const site = results.allSite.edges[0].node.siteMetadata;
  const twitter = site.social.find((option) => option.name === "twitter") || {};
  const github = site.social.find((option) => option.name === "github") || {};
  const linkedin =
    site.social.find((option) => option.name === "linkedin") || {};
  const medium = site.social.find((option) => option.name === "medium") || {};

  const pageUrl = site.siteUrl + pathname;

  const fullURL = (path: string) => (path ? `${path}` : site.siteUrl);

  // If no image is provided lets looks for a default novela static image
  image = image
    ? image
    : `https://pujasharma.associates/assets/img/PujaSharmaAndAssociates.png`;

  // Checks if the source of the image is hosted on Contentful
  if (`${image}`.includes("ctfassets")) {
    image = `${image}`;
  } else {
    image = fullURL(image);
  }

  let siteSchema = `{
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "AccountingService",
        "@id": "https://pujasharma.associates/#organization",
        "name": "Puja Sharma and Associates",
        "alternateName": "Chartered Accountant",
        "description": "Chartered Accountants firm in Jaipur providing multi-disciplinary services with an emphasis on knowledge, research, experience & professionalism.",
        "openingHours": "Mo,Tu,We,Th,Fr 10:00-18:00",
        "paymentAccepted": "Cash, Credit Card, Debit Card, Netbanking, UPI, Wallet",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Jaipur",
         "addressRegion": "Rajasthan",
         "postalCode": "302019",
         "streetAddress": "Vivekanand Marg, Nirman Nagar, DCM Ajmer Road"
        },
        "founder": {
          "@type": "Person",
          "name": "CA Puja Sharma"
        },
        "hasCredential": {
         "@type": "EducationalOccupationalCredential",
         "educationalLevel": "Chartered Accountant",
         "recognizedBy": "Institute of Chartered Accountants of India (ICAI)"
        },
        "knowsAbout": "Business and Finance, including business setup, audit and assurance, income tax, goods and service tax, regulatory compliances, bookkeeping, accounting, startup, financing, funding",
        "currenciesAccepted": "INR, USD, EUR",
        "knowsLanguage": "English, Hindi, Assamese",
        "legalName": "Puja Sharma and Associates",
        "memberOf": {
          "@type": "Organization",
          "name": "Institute of Chartered Accountants of India (ICAI)"
        },
        "telephone": "+919461528592",
        "hasMap": "https://www.google.com/maps/place/Puja+Sharma+and+Associates/@26.8931871,75.7443075,17z/data=!3m1!4b1!4m5!3m4!1s0x396db52ebfccedaf:0xfb1212ef94a8bdbd!8m2!3d26.8931871!4d75.7464962",
        "url": "https://pujasharma.associates",
        "logo": "https://pujasharma.associates/assets/img/logo.jpg",
        "latitude": "26.8931871",
        "longitude": "75.7443075",
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+919461528592",
          "contactType": "customer service",
          "areaServed": "IN",
          "availableLanguage": ["en", "Hindi", "Assamese"]
        },
        "image": "https://pujasharma.associates/assets/img/PujaSharmaAndAssociates.png",
        "email": "ca.pujasharma@outlook.com",
        "mainEntityOfPage": "https://pujasharma.associates",
        "sameAs": [
         "https://pujasharma.associates",
         "https://www.slideshare.net/PujaSharma143",
         "https://issuu.com/pujasharmaassociates"
        ]
      },
      {
        "@type": "WebSite",
        "@id": "https://pujasharma.associates/#website",
        "url": "https://pujasharma.associates",
        "name": "Puja Sharma and Associates",
        "description": "Chartered Accountants firm in Jaipur providing multi-disciplinary services with an emphasis on knowledge, research, experience & professionalism.",
        "inLanguage": "en-US"
      },
      {
        "@type": "WebPage",
        "url": "https://pujasharma.associates/blog",
        "name": "Blog | Puja Sharma and Associates",
        "isPartOf": {
          "@id": "https://pujasharma.associates/#website"
        },
        "about": {
          "@id": "https://pujasharma.associates/#organization"
        },
        "description": "Our blog is a commentary on the current industry news that we think you will benefit most from hearing about",
        "inLanguage": "en-US"
      },
      {
        "@type": "BreadcrumbList",
        "description": "Breadcrumbs list",
        "name": "Breadcrumbs",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": "1",
            "name":"Home",
            "item":{
              "@type": "Thing",
              "@id": "https://pujasharma.associates"
            }
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Blog",
            "item": {
              "@type": "Thing",
              "@id": "https://pujasharma.associates/blog"
              
            }
          }
        ]
      }
    ]
  }
`.replace(/"[^"]+"|(\s)/gm, function(matched, group1) {
    if (!group1) {
      return matched;
    } else {
      return "";
    }
  });

  let blogSchema = `{
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "name": "Puja Sharma and Associates",
        "@id": "https://pujasharma.associates/#organization",
        "alternateName": "Chartered Accountants",
        "description": "Chartered Accountants firm in Jaipur providing multi-disciplinary services with an emphasis on knowledge, research, experience & professionalism.",
        "logo":{
          "@type":"ImageObject",
          "url": "https://pujasharma.associates/assets/img/logo.jpg"
        },
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Jaipur",
         "addressRegion": "Rajasthan",
         "postalCode": "302019",
         "streetAddress": "Vivekanand Marg, Nirman Nagar, DCM Ajmer Road"
        },
        "founder": {
          "@type": "Person",
          "name": "CA Puja Sharma"
        },
        "hasCredential": {
         "@type": "EducationalOccupationalCredential",
         "educationalLevel": "Chartered Accountant",
         "recognizedBy": "Institute of Chartered Accountants of India (ICAI)"
        },
        "knowsAbout": "Business and Finance, including business setup, audit and assurance, income tax, goods and service tax, regulatory compliances, bookkeeping, accounting, startup, financing, funding",
        "legalName": "Puja Sharma and Associates",
        "memberOf": {
          "@type": "Organization",
          "name": "Institute of Chartered Accountants of India (ICAI)"
        },
        "telephone": "+919461528592",
        "url": "https://pujasharma.associates",
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+919461528592",
          "contactType": "customer service",
          "areaServed": "IN",
          "availableLanguage": ["en", "Hindi", "Assamese"]
        },
        "image": "https://pujasharma.associates/assets/img/PujaSharmaAndAssociates.png",
        "email": "ca.pujasharma@outlook.com",
        "mainEntityOfPage": "https://pujasharma.associates",
        "sameAs": [
         "https://pujasharma.associates",
         "https://www.slideshare.net/PujaSharma143",
         "https://issuu.com/pujasharmaassociates"
        ]
      },
      {
        "@type": "WebSite",
        "@id": "https://pujasharma.associates/#website",
        "url": "https://pujasharma.associates",
        "name": "Puja Sharma and Associates",
        "description": "Chartered Accountants firm in Jaipur providing multi-disciplinary services with an emphasis on knowledge, research, experience & professionalism.",
        "inLanguage": "en-US"
      },
      {
        "@type": "WebPage",
        "@id": "${articlepathName}/#webpage",
        "url": "${articlepathName}",
        "name": "${title}",
        "isPartOf": {
          "@id": "https://pujasharma.associates/#website"
        },
        "primaryImageOfPage": {
          "@id": "${image}"
        },
        "datePublished": "${dateforSEO}",
        "dateModified": "${dateforSEO}",
        "description": "${description}",
        "inLanguage": "en-US"
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "name": "Breadcrumbs",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": "1",
            "name":"Home",
            "item":{
              "@type": "Thing",
              "@id": "https://pujasharma.associates"
            }
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Blog",
            "item": {
              "@type": "Thing",
              "@id": "https://pujasharma.associates/blog"
              
            }
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": "${title}",
            "item": {
              "@type": "Thing",
              "@id": "${articlepathName}"
            }
          }
        ]
      },
      {
        "@type": "BlogPosting",
        "headline": "${title}",
        "datePublished": "${dateforSEO}",
        "dateModified": "${dateforSEO}",
        "mainEntityOfPage": {
          "@type":"WebPage",
          "@id": "${articlepathName}/#webpage"
        },
        "@id": "${articlepathName}/#article",
        "isPartOf": {
          "@id": "${articlepathName}/#webpage"
        },
        "author": {
          "@id": "${site.siteUrl}${authorsSlug}/#person"
        },
        "publisher": {
          "@id": "https://pujasharma.associates/#organization"
        },
        "image": {
          "@id": "${image}"
        },
        "inLanguage": "en-US"
      },
      {
        "@type": [
          "Person"
        ],
        "@id": "${site.siteUrl}${authorsSlug}/#person",
        "name": "${authorName}",
        "description": "${authorsBio}"
      }
    ]
  }
`.replace(/"[^"]+"|(\s)/gm, function(matched, group1) {
    if (!group1) {
      return matched;
    } else {
      return "";
    }
  });

  const schema = isBlogPost ? blogSchema : siteSchema;

  const metaTags = [
    { charset: "utf-8" },
    {
      "http-equiv": "X-UA-Compatible",
      content: "IE=edge",
    },
    {
      name: "viewport",
      content: "width=device-width, initial-scale=1",
    },
    {
      name: "theme-color",
      content: "#fff",
    },
    { itemprop: "name", content: title || site.title },
    { itemprop: "description", content: description || site.description },
    { itemprop: "image", content: image },
    { name: "description", content: description || site.description },

    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:site", content: site.name },
    { name: "twitter:title", content: title || site.title },
    { name: "twitter:description", content: description || site.description },
    {
      name: "twitter:image",
      content: image,
    },
    { name: "twitter:image:alt", content: title || site.title },

    { property: "og:type", content: "website" },
    { property: "og:title", content: title || site.title },
    { property: "og:url", content: articlepathName || pageUrl },
    { property: "og:image", content: image },
    { property: "og:description", content: description || site.description },
    { property: "og:site_name", content: site.name },
  ];

  if (published) {
    metaTags.push({ name: "article:published_time", content: published });
  }

  if (timeToRead) {
    metaTags.push({ name: "twitter:label1", value: "Reading time" });
    metaTags.push({ name: "twitter:data1", value: `${timeToRead} min read` });
  }

  if (isSecret) {
    metaTags.push({ name: "robots", content: "noindex" });
  }

  return (
    <Helmet
      title={title || site.title}
      htmlAttributes={{ lang: "en" }}
      script={themeUIDarkModeWorkaroundScript}
      meta={metaTags}
    >
      <script type="application/ld+json">{schema}</script>
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {children}
    </Helmet>
  );
};

export default SEO;
